import { Injectable } from '@angular/core'
import { di } from '@app/srv/di.service'
import { _anyToError } from '@naturalcycles/js-lib'
import { env } from '@src/environments/environment'
import { ReCaptchaV3Service } from 'ng-recaptcha'
import { firstValueFrom } from 'rxjs'
import { logUtil } from '../util/log.util'

/**
 * @description This service class is meant to make the reCAPTCHA token generation re-usable
 *
 * NOTE:
 * The Recaptcha Service tracks the user's behavior and feeds it into
 * the reCAPTCHA machine learning algorithm via the reCAPTCHA API.
 *
 * Later, we can generate a recaptcha token and pass it the recaptcha validation endpoint.
 * Which will return a score, indicating the probability that the user is a human.
 */
@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private reCaptchaV3Service?: ReCaptchaV3Service

  /**
   * @description This method is used to initialize the reCAPTCHA service.
   *
   * The call stack is as follows:
   * 1. di.get(ReCaptchaV3Service) gets called
   * 2. ReCaptchaV3Service.init gets called
   * 3. RecaptchaLoaderService.loadScript method gets called
   * 4. Two recaptcha script tags get added to the head of the document
   *    - Recaptcha api script (https://www.google.com/recaptcha/api.js)
   *    - Recaptcha api loader script (https://www.gstatic.com/recaptcha/releases/.../recaptcha__en.js)
   *
   * @linkcode
   * https://github.com/DethAriel/ng-recaptcha/blob/429e440817f17661cbf0417c2c942cab21513558/projects/ng-recaptcha/src/lib/recaptcha-v3.service.ts#L170
   *
   */
  init(): void {
    this.reCaptchaV3Service ||= di.get(ReCaptchaV3Service)
  }

  /**
   * @description Should be used to generate the reCAPTCHA token to be passed into the REST call.
   * @param actionType The action type is used to identify the reCAPTCHA token in the Google reCAPTCHA dashboard.
   *
   * Our current use cases:
   * - payments/paymentInit: The client will only receive the braintree client token if the reCAPTCHA token is valid.
   * - session/auto: The client will only be able to create an account if the reCAPTCHA token is valid.
   *
   * @returns reCAPTCHA token or undefined if reCAPTCHA is disabled
   */
  async generateRecaptchaToken(actionType: string): Promise<string | undefined> {
    // Return early if the reCAPTCHA is disabled or if the service is not initialized
    if (!env.recaptchaEnabled || !this.reCaptchaV3Service) return
    try {
      const recaptchaToken = await firstValueFrom(this.reCaptchaV3Service.execute(actionType))
      return recaptchaToken
    } catch (err) {
      logUtil.error(_anyToError(err))
      return
    }
  }
}
